import style from "@/styles/pages/Employment/Employment.module.css";
import { FormProvider, set, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "@/store/dtm";
import { setApplicationProgress } from "@/slices/applicationSlice";
import SlideTransition from "@/styles/animation/SlideTransition";
import { createApiClient } from "@/lib/Requests";
import { ApplicationDto, EmploymentDto, EmploymentModel } from "@/lib/DTMWebAPIClient.generated";
import { selectPropertyInfo } from "@/slices/propertyInfoSlice";
import SearchableDropdown from "@/components/shared/SearchableDropdown";
import InputMask from "react-input-mask";
import { AnimatePresence, motion } from "framer-motion";
import ManualAddress from "@/components/Address/ManualAddress";
import { addressInfo } from "@/slices/PreviousAddressSlice";
import { streetDirections } from "@/ts/streetDirections";
import { streetTypes } from "@/ts/streetTypes";
import { selectEmployment } from "@/slices/employmentSlice";
import { addPreviousEmployment, employerAddressInfo, employmentInfo, removePreviousEmployment, selectPreviousEmployment, setEmploymentInfo, toggleNoPreviousEmployment } from "@/slices/previousEmploymentSlice";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { currentEmploymentInfo, selectCurrentEmployment } from "@/slices/currentEmploymentSlice";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { setCachedCurrentEmployment } from "@/slices/sessionDataSlice";
import { applicationCategory } from "@/types/applicationType";
import { setClickInsightApplicationProgress, setClickInsightEvents, setClickInsightFormErrors } from "@/lib/util";

export default function PreviousEmployment() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const employment = useSelector(selectEmployment).employment;
    const [occupationCategory, setOccupationCategory] = useState<string[]>([]);

    const [isSaving, setIsSaving] = useState(false);
    const [displayError, setDisplayError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const currentEmployment = useSelector(selectCurrentEmployment).employments;
    const previousEmployment = useSelector(selectPreviousEmployment).employments;
    const noPreviousEmployment = useSelector(selectPreviousEmployment).noPreviousEmployment;
    const [empErrors, setEmpErrors] = useState<Record<string, string>[]>([]);
    const [empAddrErrors, setEmpAddrErrors] = useState<string[][]>([]);
    const [insufficientHistory, setInsufficientHistory] = useState(noPreviousEmployment);

    const [manualAddressShown, setmanualAddressShown] = useState(false);
    const [currentAddressInfo, setCurrentAddressInfo] = useState<addressInfo>({
        id: 0,
        displayId: "",
        streetNumber: "",
        streetName: "",
        streetType: "",
        streetDirection: "",
        city: "",
        province: "",
        country: "",
        postalCode: "",
        unitType: "",
        unitNumber: "",
        manualAddressEntered: true,
        yearsLivedOn: 0,
        monthsLivedOn: 0
    });
    const [addressSelected, setAddressSelected] = useState(false);
    const [addressSelectionError, setAddressSelectionError] = useState(false);
    const [focusErrorType, setFocusErrorType] = useState(0);
    const [highlightError, setHighlightError] = useState(0);

    const sessionData = useSelector(
        (state: RootState) => state.sessionData.currentSession
    );
    const propertyInfo = useSelector(selectPropertyInfo).propertyInfo;
    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });


    const methods = useForm({
        defaultValues: {
            employmentStatus: "",
            employmentType: "",
            payrollType: "",
            income: "",
            payrollFrequency: "",
            jobTitle: "",
            ownership: "",
            businessType: "",
            industryYears: "",
            industryMonths: "",
            employmentYears: "",
            employmentMonths: "",

            employerName: "",
            employerPhoneNumber: "",
            autoAddress: "",
            streetNumber: "",
            streetName: "",
            streetType: "",
            streetDirection: "",
            unitType: "",
            unitNumber: "",
            cityName: "",
            provinceName: "",
            postalCode: "",
            country: ""
        },
        shouldFocusError: true,
    });

    const fields = [
        {
            element: "search",
            field: "",
            mode: window.pca?.fieldMode.SEARCH,
        },
        {
            element: "span-street-address",
            field: "Line1",
            mode: window.pca?.fieldMode.POPULATE,
        },
        {
            element: "span-street-address2",
            field: "Line2",
            mode: window.pca?.fieldMode?.POPULATE,
        },
        { element: "city", field: "City", mode: window.pca?.fieldMode.POPULATE },
        {
            element: "span-state",
            field: "ProvinceCode",
            mode: window.pca?.fieldMode.POPULATE,
        },
        {
            element: "span-postcode",
            field: "PostalCode",
            mode: window.pca?.fieldMode.POPULATE,
        },
        {
            element: "span-country",
            field: "CountryName",
            mode: window.pca?.fieldMode.POPULATE,
        },
    ];
    const options = {
        key: "XD95-YY77-BP99-EM55",
    };

    useEffect(() => {
        document.title = "Enter Employment Details";
        setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);
        dispatch(setCachedCurrentEmployment(currentEmployment));

        client.apiApplicationsFetch().then((result) => {
            result.application = application;
            result.employment = employment;
            result.currentEmployment = currentEmployment;
            result.propertyInfo = propertyInfo;
            client.apiApplicationsSave(new ApplicationDto({
                applicationData: JSON.stringify(result)
            })).catch((error) => {
                console.log("Error saving application info");
            });
        }).catch((error) => {
            console.log("Error fetching application info", error);
        });
    }, []);


    useEffect(() => {
        let newEmpErrors: Record<string, string>[] = [];
        previousEmployment.forEach((emp: employmentInfo, index: any) => {
            let id = index + 1;
            let fields = [
                {
                    element: "empAddressSearch" + id,
                    field: "",
                    mode: window.pca?.fieldMode.SEARCH,
                },
                {
                    element: "span-street-address-" + id,
                    field: "Line1",
                    mode: window.pca?.fieldMode.POPULATE,
                },
                {
                    element: "span-street-address2-" + id,
                    field: "Line2",
                    mode: window.pca?.fieldMode?.POPULATE,
                },
                { element: "city-" + id, field: "City", mode: window.pca?.fieldMode.POPULATE },
                {
                    element: "span-state-" + id,
                    field: "ProvinceName",
                    mode: window.pca?.fieldMode.POPULATE,
                },
                {
                    element: "",
                    field: "PostalCode",
                    mode: window.pca?.fieldMode.POPULATE,
                },
                {
                    element: "span-country-" + id,
                    field: "CountryName",
                    mode: window.pca?.fieldMode.POPULATE,
                },
            ];
            let control = new window.pca.Address(fields, options);
            control.listen("populate", (address: any) => {
                let streetInfo = address.Line1.split(" ");
                let streetName = "";
                let streetDirection = "";
                let streetType = "";
                let streetComponents: string[] = address.Street.split(" ");
                let lastStreetComponent = streetComponents[streetComponents.length - 1].toUpperCase();

                //Check if street direction and type exists in the street name
                if (streetDirections.includes(lastStreetComponent)) {
                    streetDirection = lastStreetComponent;
                    let secondLastStreetComponent = streetComponents[streetComponents.length - 2].toUpperCase();
                    if (streetTypes.includes(secondLastStreetComponent)) {
                        streetType = secondLastStreetComponent;
                    }
                    streetComponents.forEach((value, index) => {
                        if (index < streetComponents.length - 2) {
                            streetName += value + " "
                        }
                    });
                } else if (streetTypes.includes(lastStreetComponent)) {
                    streetType = lastStreetComponent;
                    streetComponents.forEach((value, index) => {
                        if (index != streetComponents.length - 1) {
                            streetName += value + " "
                        }
                    });
                } else {
                    streetComponents.forEach((value, index) => {
                        console.log(value, index);
                        streetName += value + " "
                    });
                }
                streetName = streetName.trim();

                //Check if unit number is embeded in the street number
                let streetNumber = "";
                let streetNumberComponents = streetInfo[0].split("-");
                if (streetNumberComponents.length > 1) {
                    streetNumber = streetNumberComponents[streetNumberComponents.length - 1];
                } else {
                    streetNumber = streetInfo[0];
                }

                let newEmploymentInfo: employmentInfo = {
                    displayId: emp.displayId,
                    id: emp.id,
                    status: emp.status,
                    type: emp.type,
                    occupationIndustry: emp.occupationIndustry,
                    occupation: emp.occupation,
                    jobTitle: emp.jobTitle,
                    ownershipType: emp.ownershipType,
                    businessType: emp.businessType,
                    industryYears: emp.industryYears,
                    industryMonths: emp.industryMonths,
                    employmentYears: emp.employmentYears,
                    employmentMonths: emp.employmentMonths,
                    notWorkingYears: emp.notWorkingYears,
                    notWorkingMonths: emp.notWorkingMonths,

                    employerName: emp.employerName,
                    employerPhone: emp.employerPhone,
                    employerAddress: {
                        displayId: emp.employerAddress.displayId,
                        id: emp.employerAddress.id,
                        streetNumber: streetNumber,
                        streetName: streetName,
                        streetType: streetType,
                        streetDirection: streetDirection,
                        city: address.City,
                        province: address.ProvinceCode,
                        country: address.CountryName,
                        postalCode: address.PostalCode,
                        unitType: address.SubBuilding != undefined && address.SubBuilding != "" ? "unit" : "",
                        unitNumber: address.SubBuilding != undefined ? address.SubBuilding : ""
                    },
                    manualAddressEntered: emp.manualAddressEntered,
                }
                //If Street Type is not in the master list, toggle manual address entry
                if (!streetTypes.includes(newEmploymentInfo.employerAddress.streetType)) {
                    newEmploymentInfo.manualAddressEntered = true;
                }

                dispatch(setEmploymentInfo(newEmploymentInfo));

                if (empErrors[parseInt(id) - 1]["autoAddress"] != undefined) {
                    let newEmpErrors = [...empErrors];
                    delete newEmpErrors[parseInt(id) - 1]["autoAddress"];
                    delete newEmpErrors[parseInt(id) - 1]["streetNumber"];
                    delete newEmpErrors[parseInt(id) - 1]["streetName"];
                    delete newEmpErrors[parseInt(id) - 1]["streetType"];
                    delete newEmpErrors[parseInt(id) - 1]["streetDirection"];
                    delete newEmpErrors[parseInt(id) - 1]["city"];
                    delete newEmpErrors[parseInt(id) - 1]["province"];
                    delete newEmpErrors[parseInt(id) - 1]["postalCode"];
                    delete newEmpErrors[parseInt(id) - 1]["country"];
                    setEmpErrors(newEmpErrors);
                }

            });
            let errors: Record<string, string> = {};
            if (empErrors[index] != undefined) {
                newEmpErrors[index] = empErrors[index];
            } else {
                newEmpErrors.push(errors);
            }
        });
        setEmpErrors(newEmpErrors);

    }, [previousEmployment])

    useEffect(() => {
        if (!manualAddressShown) {
            methods.unregister([
                "streetNumber",
                "streetName",
                "streetType",
                "unitNumber",
                "cityName",
                "provinceName",
                "postalCode",
                "country",
            ]);
        } else {
            //methods.setValue("autoAddress", "");
            if (addressSelected && currentAddressInfo) {
                methods.setValue("streetNumber", currentAddressInfo.streetNumber);
                methods.setValue("streetName", currentAddressInfo.streetName);
                methods.setValue("streetType", currentAddressInfo.streetType);
                methods.setValue("streetDirection", currentAddressInfo.streetDirection);
                methods.setValue("cityName", currentAddressInfo.city);
                methods.setValue("provinceName", currentAddressInfo.province);
                methods.setValue("postalCode", currentAddressInfo.postalCode);
                methods.setValue("country", currentAddressInfo.country);
            } else {
                methods.setValue("streetNumber", "");
                methods.setValue("streetName", "");
                methods.setValue("streetType", "");
                methods.setValue("streetDirection", "");
                methods.setValue("cityName", "");
                methods.setValue("provinceName", "");
                methods.setValue("postalCode", "");
                methods.setValue("country", "");
            }

            methods.unregister("autoAddress");
            methods.register("streetNumber", {
                required: "Enter your street number",
                pattern: {
                    value: /^[ #a-zA-Z0-9._-]+$/,
                    message: " Please enter a valid street number",
                },
            });
            methods.register("unitNumber", {
                required: methods.getValues("unitType").trim() != "" ? "Enter your unit number" : false,
                pattern: {
                    value: /^[ #a-zA-Z0-9._-]+$/,
                    message:
                        " Please enter a valid unit number. Only letters, numbers and _-.# are accepted",
                },
            });
            methods.register("streetName", {
                required: "Enter your street name",
                pattern: {
                    value: /^[ #a-zA-ZÀ-Ÿ0-9._-]+$/,
                    message: " Please enter a valid street name",
                },
            });
            methods.register("streetType", {
                required: "Select your street type"
            });
            methods.register("cityName", {
                required: "Enter your city name",
                pattern: {
                    value: /^[ #a-zA-ZÀ-Ÿ0-9._-]+$/,
                    message: " Please enter a valid city name",
                },
            });
            methods.register("provinceName", {
                required: "Select your province name",
            });
            methods.register("postalCode", {
                required: "Enter your postal code",
            });
            methods.register("country", {
                required: "Select your country name",
            });
        }

        //methods.setFocus("occupation")
    }, [manualAddressShown, methods]);

    const onError = (errors: object) => {
        console.log(errors);
        setClickInsightFormErrors(errors);
        //setDisplayError(true);
        setHighlightError(0);
    }

    const onSubmit = async (data: any) => {
        //Check that the three year minimum is met (or that the checkbox is selected)
        let totalEmploymentTime = 0;
        let highestEmploymentTime = 0;
        currentEmployment.forEach((c: currentEmploymentInfo) => {
            if (c.employmentYears != undefined && c.employmentMonths != undefined) {
                if (((c.employmentYears * 12) + c.employmentMonths) > highestEmploymentTime) {
                    highestEmploymentTime = ((c.employmentYears * 12) + c.employmentMonths);
                } else if (((c.notWorkingYears ? (c.notWorkingYears * 12) : 0) + (c.notWorkingMonths ? c.notWorkingMonths : 0)) > highestEmploymentTime) {
                    highestEmploymentTime = (c.notWorkingYears ? (c.notWorkingYears * 12) : 0) + (c.notWorkingMonths ? c.notWorkingMonths : 0);
                }
                // totalEmploymentTime += (c.employmentYears * 12) + c.employmentMonths;
                // totalEmploymentTime += (c.notWorkingYears ? (c.notWorkingYears * 12) : 0) + (c.notWorkingMonths ? c.notWorkingMonths : 0);
            }
        });

        let emptyFieldExists = false;
        let employmentErrors: Record<string, string>[] = [];
        totalEmploymentTime += highestEmploymentTime;

        previousEmployment.forEach((p, index) => {
            totalEmploymentTime += (p.employmentYears ? (p.employmentYears * 12) : 0) + (p.employmentMonths ? p.employmentMonths : 0);
            totalEmploymentTime += (p.notWorkingYears ? (p.notWorkingYears * 12) : 0) + (p.notWorkingMonths ? p.notWorkingMonths : 0);
            let errors: Record<string, string> = {};
            if (p.status.trim() == "") {
                emptyFieldExists = true;
                errors["employmentStatus"] = "Select an option.";
            }

            if (p.status.trim() == "employed" || p.status.trim() == "selfemployed") {
                if (p.type.trim() == "") {
                    emptyFieldExists = true;
                    errors["employmentType"] = "Select an option.";
                }
                if (p.occupation.trim() == "") {
                    emptyFieldExists = true;
                    errors["occupation"] = "Select an occupation.";
                }
                if (p.jobTitle.trim() == "") {
                    emptyFieldExists = true;
                    errors["jobTitle"] = "Enter a job title.";
                }
                if (p.industryYears == undefined) {
                    emptyFieldExists = true;
                    errors["industryLengthYears"] = "Enter the number of years.";
                }
                if (p.industryMonths == undefined) {
                    emptyFieldExists = true;
                    errors["industryLengthMonths"] = "Enter the number of months.";
                }
                if (p.employmentYears == undefined) {
                    emptyFieldExists = true;
                    errors["employmentYears"] = "Enter the number of years.";
                }
                if (p.employmentMonths == undefined) {
                    emptyFieldExists = true;
                    errors["employmentMonths"] = "Enter the number of months.";
                }
                if (p.employerName.trim() == "") {
                    emptyFieldExists = true;
                    errors["employerName"] = "Enter your employer name.";
                }
                if (p.employerPhone.trim() == "") {
                    emptyFieldExists = true;
                    errors["employerPhoneNumber"] = "Enter your employer phone number.";
                } else {
                    if (!/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/.test(p.employerPhone.trim())) {
                        emptyFieldExists = true;
                        errors["employerPhoneNumber"] = "Enter a valid phone number.";
                    }
                }

                if (p.status.trim() == "selfemployed" && p.ownershipType.trim() == "") {
                    emptyFieldExists = true;
                    errors["ownership"] = "Select an option.";
                }
                if (p.status.trim() == "selfemployed" && p.businessType.trim() == "") {
                    emptyFieldExists = true;
                    errors["businessType"] = "Enter a business type.";
                }
            }

            if (p.status.trim() == "employed" || p.status.trim() == "selfemployed") {

                let empAddress = p.employerAddress;
                if (!p.manualAddressEntered && (empAddress.streetNumber.trim() == "" || empAddress.streetName.trim() == "" || empAddress.city.trim() == "" || empAddress.country.trim() == "")) {
                    emptyFieldExists = true;
                    errors["autoAddress"] = "Select an address";
                }
                if (empAddress.streetNumber.trim() == "") {
                    emptyFieldExists = true;
                    errors["streetNumber"] = "Enter your street number.";
                }
                if (empAddress.streetType.trim() == "") {
                    emptyFieldExists = true;
                    errors["streetType"] = "Select a street type.";
                }
                if (empAddress.streetName.trim() == "") {
                    emptyFieldExists = true;
                    errors["streetName"] = "Enter your street name.";
                }
                if (empAddress.unitType.trim() != "" && empAddress.unitNumber.trim() == "") {
                    emptyFieldExists = true;
                    errors["unitNumber"] = "Enter your unit number.";
                }
                if (empAddress.unitNumber.trim() != "" && empAddress.unitType.trim() == "") {
                    emptyFieldExists = true;
                    errors["unitType"] = "Enter your unit type.";
                }
                if (empAddress.city.trim() == "") {
                    emptyFieldExists = true;
                    errors["city"] = "Enter your city name.";
                }
                if (empAddress.province.trim() == "" && (empAddress.country.trim() == "Canada" || empAddress.country.trim() == "United States")) {
                    emptyFieldExists = true;
                    errors["province"] = "Enter your province name.";
                }
                if (empAddress.postalCode.trim() == "" && (empAddress.country.trim() == "Canada" || empAddress.country.trim() == "United States")) {
                    emptyFieldExists = true;
                    errors["postalCode"] = "Enter your postal code.";
                }
                if (empAddress.country.trim() == "") {
                    emptyFieldExists = true;
                    errors["country"] = "Enter your country.";
                }
            }
            if (p.status.trim() == "retired" || p.status.trim() == "unemployed") {
                if (p.notWorkingYears == undefined) {
                    emptyFieldExists = true;
                    errors["notWorkingYears"] = "Enter the number of years.";
                }
                if (p.notWorkingMonths == undefined) {
                    emptyFieldExists = true;
                    errors["notWorkingMonths"] = "Enter the number of months.";
                }
            }
            employmentErrors.push(errors);
        });
        setEmpErrors(employmentErrors);
        if (totalEmploymentTime < 36 && !insufficientHistory) {
            setErrorMessage("Three years of employment history is required.");
            setDisplayError(true);
            window.scroll(0, 0);
            return;
        }
        if (emptyFieldExists) {
            setErrorMessage("One or more fields is missing in your employer(s).");
            setDisplayError(true);
            window.scroll(0, 0);
            console.log(employmentErrors);
            return;
        }

        setIsSaving(true);

        let prevEmployment: EmploymentDto[] = [];
        previousEmployment.forEach((p) => {
            let nonWorkingMonths = (p.notWorkingYears ? (p.notWorkingYears * 12) : 0) + (p.notWorkingMonths ? p.notWorkingMonths : 0);
            prevEmployment.push(new EmploymentDto({
                id: p.id,
                employmentType_Name: p.status,
                employmentPositionType_Name: p.type,
                jobTitle: p.jobTitle,
                employer_Name: p.employerName,
                employer_Phone: p.employerPhone,
                employer_Address_StreetNumber: p.employerAddress.streetNumber,
                employer_Address_StreetName: p.employerAddress.streetName,
                employer_Address_StreetType: p.employerAddress.streetType,
                employer_Address_StreetDirection: p.employerAddress.streetDirection,
                employer_Address_UnitType: p.employerAddress.unitType,
                employer_Address_UnitNumber: p.employerAddress.unitNumber,
                employer_Address_City: p.employerAddress.city,
                employer_Address_PostalCode: p.employerAddress.postalCode,
                employer_Address_Region: p.employerAddress.province,
                employer_Address_Country: p.employerAddress.country,
                monthsAtEmployment: (p.status == "retired" || p.status == "unemployed") ? nonWorkingMonths
                    : (p.employmentYears ? (p.employmentYears * 12) : 0) + (p.employmentMonths ? p.employmentMonths : 0),
                monthsInIndustry: (p.industryYears ? (p.industryYears * 12) : 0) + (p.industryMonths ? p.industryMonths : 0),
                totalMonthsAtEmployment: (p.status == "retired" || p.status == "unemployed") ? nonWorkingMonths
                    : (p.employmentYears ? (p.employmentYears * 12) : 0) + (p.employmentMonths ? p.employmentMonths : 0),
                totalMonthsInIndustry: (p.industryYears ? (p.industryYears * 12) : 0) + (p.industryMonths ? p.industryMonths : 0),
                employer_Occupation_Industry: p.occupationIndustry,
                employer_Occupation_Name: p.occupation,
                ownership_OwnershipType_Name: p.ownershipType,
                ownership_BusinessType: p.businessType,
                previousEmployment: true,
            }));
        });


        await client.apiEmploymentsPrevious(new EmploymentModel({
            applicantId: applicant.id,
            dealId: application.id,
            employment: prevEmployment
        })).then((result) => {
            console.log(result);
            if (result != undefined && result.length > 0) {
                result.forEach((emp, index) => {
                    previousEmployment.forEach((p) => {
                        if (p.occupation == emp.employer_Occupation_Name && p.jobTitle == emp.jobTitle) {
                            let newEmp: employmentInfo = {
                                id: emp.id ? emp.id : 0,
                                displayId: p.displayId,
                                status: p.status,
                                type: p.type,
                                occupationIndustry: p.occupationIndustry,
                                occupation: p.occupation,
                                jobTitle: p.jobTitle,
                                ownershipType: p.ownershipType,
                                businessType: p.businessType,
                                industryYears: p.industryYears,
                                industryMonths: p.industryMonths,
                                employmentYears: p.employmentYears,
                                employmentMonths: p.employmentMonths,
                                notWorkingYears: p.notWorkingYears,
                                notWorkingMonths: p.notWorkingMonths,
                                employerName: p.employerName,
                                employerPhone: p.employerPhone,
                                employerAddress: p.employerAddress,
                                manualAddressEntered: p.manualAddressEntered,
                            }
                            dispatch(setEmploymentInfo(newEmp));
                        }
                    });
                });
            }
            dispatch(setApplicationProgress(12));
            setClickInsightApplicationProgress('Build your profile - What is your employment history?');
            if (application.applicationCategory !== applicationCategory.Buy) {
                navigate("/single/refi-property-details");
            } else {
                navigate("/single/assets");
            }

        }).catch((error) => {
            setIsSaving(false);
            console.log("Error saving employment details", error);
        });
    };

    const toggleManualAddress = (id: string) => {
        previousEmployment.forEach(p => {
            if (p.displayId == id) {
                let newEmploymentInfo: employmentInfo = {
                    displayId: p.displayId,
                    id: p.id,
                    status: p.status,
                    type: p.type,
                    occupation: p.occupation,
                    occupationIndustry: p.occupationIndustry,
                    jobTitle: p.jobTitle,
                    ownershipType: p.ownershipType,
                    businessType: p.businessType,
                    industryYears: p.industryYears,
                    industryMonths: p.industryMonths,
                    employmentYears: p.employmentYears,
                    employmentMonths: p.employmentMonths,
                    notWorkingYears: p.notWorkingYears,
                    notWorkingMonths: p.notWorkingMonths,

                    employerName: p.employerName,
                    employerPhone: p.employerPhone,
                    employerAddress: p.employerAddress,
                    manualAddressEntered: !p.manualAddressEntered,
                }

                dispatch(setEmploymentInfo(newEmploymentInfo));
            }
        });
    };

    const getAddressData = (id: any, type: any, val: any) => {
        let addressData = { ...previousEmployment[id - 1] };

        let newEmpAddressData = { ...addressData.employerAddress }

        switch (type) {
            case "streetNumber":
                newEmpAddressData.streetNumber = val;
                break;
            case "streetName":
                newEmpAddressData.streetName = val;
                break;
            case "streetType":
                newEmpAddressData.streetType = val;
                break;
            case "streetDirection":
                newEmpAddressData.streetDirection = val;
                break;
            case "unitNumber":
                newEmpAddressData.unitNumber = val;
                break;
            case "unitType":
                newEmpAddressData.unitType = val;
                break;
            case "cityName":
                newEmpAddressData.city = val;
                break;
            case "province":
                newEmpAddressData.province = val;
                break;
            case "postalCode":
                newEmpAddressData.postalCode = val;
                break;
            case "country":
                newEmpAddressData.country = val;
                //Clear the province/state if country is updated
                newEmpAddressData.province = "";
                break;
        }
        setEmployerAddress(addressData.displayId, newEmpAddressData);

        if (empErrors[parseInt(id) - 1]["streetNumber"] != undefined && type == "streetNumber" && val.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["streetNumber"];
            setEmpErrors(newEmpErrors);
        }
        if (empErrors[parseInt(id) - 1]["streetName"] != undefined && type == "streetName" && val.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["streetName"];
            setEmpErrors(newEmpErrors);
        }
        if (empErrors[parseInt(id) - 1]["streetType"] != undefined && type == "streetType" && val.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["streetType"];
            setEmpErrors(newEmpErrors);
        }
        if (empErrors[parseInt(id) - 1]["city"] != undefined && type == "cityName" && val.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["city"];
            setEmpErrors(newEmpErrors);
        }
        if (empErrors[parseInt(id) - 1]["province"] != undefined && type == "province" && val.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["province"];
            setEmpErrors(newEmpErrors);
        }
        if (empErrors[parseInt(id) - 1]["postalCode"] != undefined && type == "postalCode" && val.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["postalCode"];
            setEmpErrors(newEmpErrors);
        }
        if (empErrors[parseInt(id) - 1]["country"] != undefined && type == "country" && val.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["country"];
            setEmpErrors(newEmpErrors);
        }

        //If Street Number, Steet Name and City are Filled, Clear AutoAddress Error (if it exists)
        if (newEmpAddressData.streetNumber.trim() != "" && newEmpAddressData.streetName.trim() != "" 
            && newEmpAddressData.streetType.trim() != "" && newEmpAddressData.city.trim() != "") {
            let newEmpErrors = [...empErrors];
            delete newEmpErrors[parseInt(id) - 1]["autoAddress"];
            setEmpErrors(newEmpErrors);
        }
    }

    const addPrevEmployment = () => {
        let employmentId = (previousEmployment.length + 1).toString();

        let newEmploymentInfo: employmentInfo = {
            id: 0,
            displayId: employmentId,
            status: "",
            type: "",
            occupation: "",
            occupationIndustry: "",
            jobTitle: "",
            ownershipType: "",
            businessType: "",
            industryYears: undefined,
            industryMonths: undefined,
            employmentYears: undefined,
            employmentMonths: undefined,
            notWorkingYears: undefined,
            notWorkingMonths: undefined,

            employerName: "",
            employerPhone: "",
            employerAddress: {
                displayId: employmentId,
                id: 0,
                streetNumber: "",
                streetName: "",
                streetType: "",
                streetDirection: "",
                unitType: "",
                unitNumber: "",
                city: "",
                province: "",
                postalCode: "",
                country: "",
            },
            manualAddressEntered: false,
        }
        dispatch(addPreviousEmployment(newEmploymentInfo));
    }

    const setEmploymentStatus = (id: string, newStatus: string) => {
        previousEmployment.forEach(p => {
            if (p.displayId == id) {
                let newEmploymentInfo: employmentInfo = {
                    displayId: p.displayId,
                    id: p.id,
                    status: newStatus,
                    type: p.type,
                    occupation: p.occupation,
                    occupationIndustry: p.occupationIndustry,
                    jobTitle: p.jobTitle,
                    ownershipType: p.ownershipType,
                    businessType: p.businessType,
                    industryYears: p.industryYears,
                    industryMonths: p.industryMonths,
                    employmentYears: p.employmentYears,
                    employmentMonths: p.employmentMonths,
                    notWorkingYears: p.notWorkingYears,
                    notWorkingMonths: p.notWorkingMonths,

                    employerName: p.employerName,
                    employerPhone: p.employerPhone,
                    employerAddress: p.employerAddress,
                    manualAddressEntered: p.manualAddressEntered,
                }
                dispatch(setEmploymentInfo(newEmploymentInfo));

                if (empErrors[parseInt(id) - 1]["employmentStatus"] != undefined && newStatus.trim() != "") {
                    let newEmpErrors = [...empErrors];
                    delete newEmpErrors[parseInt(id) - 1]["employmentStatus"];
                    setEmpErrors(newEmpErrors);
                }
            }
        });
    };

    const setEmploymentType = (id: string, newType: string) => {
        previousEmployment.forEach(p => {
            if (p.displayId == id) {
                let newEmploymentInfo: employmentInfo = {
                    displayId: p.displayId,
                    id: p.id,
                    status: p.status,
                    type: newType,
                    occupation: p.occupation,
                    occupationIndustry: p.occupationIndustry,
                    jobTitle: p.jobTitle,
                    ownershipType: p.ownershipType,
                    businessType: p.businessType,
                    industryYears: p.industryYears,
                    industryMonths: p.industryMonths,
                    employmentYears: p.employmentYears,
                    employmentMonths: p.employmentMonths,
                    notWorkingYears: p.notWorkingYears,
                    notWorkingMonths: p.notWorkingMonths,

                    employerName: p.employerName,
                    employerPhone: p.employerPhone,
                    employerAddress: p.employerAddress,
                    manualAddressEntered: p.manualAddressEntered,
                }
                dispatch(setEmploymentInfo(newEmploymentInfo));

                if (empErrors[parseInt(id) - 1]["employmentType"] != undefined && newType.trim() != "") {
                    let newEmpErrors = [...empErrors];
                    delete newEmpErrors[parseInt(id) - 1]["employmentType"];
                    setEmpErrors(newEmpErrors);
                }
            }
        });
    };


    const setOccupation = (id: string, newOccupation: string, newIndustry: string) => {
        previousEmployment.forEach(p => {
            if (p.displayId == id) {
                let newEmploymentInfo: employmentInfo = {
                    displayId: p.displayId,
                    id: p.id,
                    status: p.status,
                    type: p.type,
                    occupation: newOccupation,
                    occupationIndustry: newIndustry,
                    jobTitle: p.jobTitle,
                    ownershipType: p.ownershipType,
                    businessType: p.businessType,
                    industryYears: p.industryYears,
                    industryMonths: p.industryMonths,
                    employmentYears: p.employmentYears,
                    employmentMonths: p.employmentMonths,
                    notWorkingYears: p.notWorkingYears,
                    notWorkingMonths: p.notWorkingMonths,

                    employerName: p.employerName,
                    employerPhone: p.employerPhone,
                    employerAddress: p.employerAddress,
                    manualAddressEntered: p.manualAddressEntered,
                }
                dispatch(setEmploymentInfo(newEmploymentInfo));

                if (empErrors[parseInt(id) - 1]["occupation"] != undefined && newOccupation.trim() != "") {
                    let newEmpErrors = [...empErrors];
                    delete newEmpErrors[parseInt(id) - 1]["occupation"];
                    setEmpErrors(newEmpErrors);
                }
            }
        });
    };

    const setJobTitle = (id: string, newTitle: string) => {
        previousEmployment.forEach(p => {
            if (p.displayId == id) {
                let newEmploymentInfo: employmentInfo = {
                    displayId: p.displayId,
                    id: p.id,
                    status: p.status,
                    type: p.type,
                    occupation: p.occupation,
                    occupationIndustry: p.occupationIndustry,
                    jobTitle: newTitle,
                    ownershipType: p.ownershipType,
                    businessType: p.businessType,
                    industryYears: p.industryYears,
                    industryMonths: p.industryMonths,
                    employmentYears: p.employmentYears,
                    employmentMonths: p.employmentMonths,
                    notWorkingYears: p.notWorkingYears,
                    notWorkingMonths: p.notWorkingMonths,

                    employerName: p.employerName,
                    employerPhone: p.employerPhone,
                    employerAddress: p.employerAddress,
                    manualAddressEntered: p.manualAddressEntered,
                }
                dispatch(setEmploymentInfo(newEmploymentInfo));

                if (empErrors[parseInt(id) - 1]["jobTitle"] != undefined && newTitle.trim() != "") {
                    let newEmpErrors = [...empErrors];
                    delete newEmpErrors[parseInt(id) - 1]["jobTitle"];
                    setEmpErrors(newEmpErrors);
                }
            }
        });
    };

    const setOwnership = (id: string, newOwnership: string) => {
        previousEmployment.forEach(p => {
            if (p.displayId == id) {
                let newEmploymentInfo: employmentInfo = {
                    displayId: p.displayId,
                    id: p.id,
                    status: p.status,
                    type: p.type,
                    occupation: p.occupation,
                    occupationIndustry: p.occupationIndustry,
                    jobTitle: p.jobTitle,
                    ownershipType: newOwnership,
                    businessType: p.businessType,
                    industryYears: p.industryYears,
                    industryMonths: p.industryMonths,
                    employmentYears: p.employmentYears,
                    employmentMonths: p.employmentMonths,
                    notWorkingYears: p.notWorkingYears,
                    notWorkingMonths: p.notWorkingMonths,

                    employerName: p.employerName,
                    employerPhone: p.employerPhone,
                    employerAddress: p.employerAddress,
                    manualAddressEntered: p.manualAddressEntered,
                }
                dispatch(setEmploymentInfo(newEmploymentInfo));
            }
        });
    };

    const setIndustryLength = (id: string, years?: string, months?: string) => {
        previousEmployment.forEach(p => {
            if (p.displayId == id) {
                let newEmploymentInfo: employmentInfo = {
                    displayId: p.displayId,
                    id: p.id,
                    status: p.status,
                    type: p.type,
                    occupation: p.occupation,
                    occupationIndustry: p.occupationIndustry,
                    jobTitle: p.jobTitle,
                    ownershipType: p.ownershipType,
                    businessType: p.businessType,
                    industryYears: years != undefined ? +years : p.industryYears,
                    industryMonths: months != undefined ? +months : p.industryMonths,
                    employmentYears: p.employmentYears,
                    employmentMonths: p.employmentMonths,
                    notWorkingYears: p.notWorkingYears,
                    notWorkingMonths: p.notWorkingMonths,

                    employerName: p.employerName,
                    employerPhone: p.employerPhone,
                    employerAddress: p.employerAddress,
                    manualAddressEntered: p.manualAddressEntered,
                }
                dispatch(setEmploymentInfo(newEmploymentInfo));

                if (empErrors[parseInt(id) - 1]["industryLengthYears"] != undefined && (years?.trim() != "")) {
                    let newEmpErrors = [...empErrors];
                    delete newEmpErrors[parseInt(id) - 1]["industryLengthYears"];
                    setEmpErrors(newEmpErrors);
                }
                if (empErrors[parseInt(id) - 1]["industryLengthMonths"] != undefined && (months?.trim() != "")) {
                    let newEmpErrors = [...empErrors];
                    delete newEmpErrors[parseInt(id) - 1]["industryLengthMonths"];
                    setEmpErrors(newEmpErrors);
                }
            }
        });
    };

    const setEmploymentLength = (id: string, years?: string, months?: string) => {
        previousEmployment.forEach(p => {
            if (p.displayId == id) {
                let newEmploymentInfo: employmentInfo = {
                    displayId: p.displayId,
                    id: p.id,
                    status: p.status,
                    type: p.type,
                    occupation: p.occupation,
                    occupationIndustry: p.occupationIndustry,
                    jobTitle: p.jobTitle,
                    ownershipType: p.ownershipType,
                    businessType: p.businessType,
                    industryYears: p.industryYears,
                    industryMonths: p.industryMonths,
                    employmentYears: years != undefined ? +years : p.employmentYears,
                    employmentMonths: months != undefined ? +months : p.employmentMonths,
                    notWorkingYears: p.notWorkingYears,
                    notWorkingMonths: p.notWorkingMonths,

                    employerName: p.employerName,
                    employerPhone: p.employerPhone,
                    employerAddress: p.employerAddress,
                    manualAddressEntered: p.manualAddressEntered,
                }
                dispatch(setEmploymentInfo(newEmploymentInfo));

                if (empErrors[parseInt(id) - 1]["employmentYears"] != undefined && (years?.trim() != "")) {
                    let newEmpErrors = [...empErrors];
                    delete newEmpErrors[parseInt(id) - 1]["employmentYears"];
                    setEmpErrors(newEmpErrors);
                }
                if (empErrors[parseInt(id) - 1]["employmentMonths"] != undefined && (months?.trim() != "")) {
                    let newEmpErrors = [...empErrors];
                    delete newEmpErrors[parseInt(id) - 1]["employmentMonths"];
                    setEmpErrors(newEmpErrors);
                }
            }
        });
    };

    const setEmployerName = (id: string, newName: string) => {
        previousEmployment.forEach(p => {
            if (p.displayId == id) {
                let newEmploymentInfo: employmentInfo = {
                    displayId: p.displayId,
                    id: p.id,
                    status: p.status,
                    type: p.type,
                    occupation: p.occupation,
                    occupationIndustry: p.occupationIndustry,
                    jobTitle: p.jobTitle,
                    ownershipType: p.ownershipType,
                    businessType: p.businessType,
                    industryYears: p.industryYears,
                    industryMonths: p.industryMonths,
                    employmentYears: p.employmentYears,
                    employmentMonths: p.employmentMonths,
                    notWorkingYears: p.notWorkingYears,
                    notWorkingMonths: p.notWorkingMonths,

                    employerName: newName,
                    employerPhone: p.employerPhone,
                    employerAddress: p.employerAddress,
                    manualAddressEntered: p.manualAddressEntered,
                }
                dispatch(setEmploymentInfo(newEmploymentInfo));

                if (empErrors[parseInt(id) - 1]["employerName"] != undefined && newName.trim() != "") {
                    let newEmpErrors = [...empErrors];
                    delete newEmpErrors[parseInt(id) - 1]["employerName"];
                    setEmpErrors(newEmpErrors);
                }
            }
        });
    };

    const setEmployerPhone = (id: string, newPhone: string) => {
        previousEmployment.forEach(p => {
            if (p.displayId == id) {
                let newEmploymentInfo: employmentInfo = {
                    displayId: p.displayId,
                    id: p.id,
                    status: p.status,
                    type: p.type,
                    occupation: p.occupation,
                    occupationIndustry: p.occupationIndustry,
                    jobTitle: p.jobTitle,
                    ownershipType: p.ownershipType,
                    businessType: p.businessType,
                    industryYears: p.industryYears,
                    industryMonths: p.industryMonths,
                    employmentYears: p.employmentYears,
                    employmentMonths: p.employmentMonths,
                    notWorkingYears: p.notWorkingYears,
                    notWorkingMonths: p.notWorkingMonths,

                    employerName: p.employerName,
                    employerPhone: newPhone,
                    employerAddress: p.employerAddress,
                    manualAddressEntered: p.manualAddressEntered,
                }
                dispatch(setEmploymentInfo(newEmploymentInfo));

                //Remove error from field, if it exists
                if (empErrors[parseInt(id) - 1]["employerPhoneNumber"] != undefined && newPhone.trim() != "") {
                    let newEmpErrors = [...empErrors];
                    delete newEmpErrors[parseInt(id) - 1]["employerPhoneNumber"];
                    setEmpErrors(newEmpErrors);
                }
            }
        });
    };

    const setEmployerAddress = (id: string, newAddress: employerAddressInfo) => {
        previousEmployment.forEach(p => {
            if (p.displayId == id) {
                let newEmploymentInfo: employmentInfo = {
                    displayId: p.displayId,
                    id: p.id,
                    status: p.status,
                    type: p.type,
                    occupation: p.occupation,
                    occupationIndustry: p.occupationIndustry,
                    jobTitle: p.jobTitle,
                    ownershipType: p.ownershipType,
                    businessType: p.businessType,
                    industryYears: p.industryYears,
                    industryMonths: p.industryMonths,
                    employmentYears: p.employmentYears,
                    employmentMonths: p.employmentMonths,
                    notWorkingYears: p.notWorkingYears,
                    notWorkingMonths: p.notWorkingMonths,

                    employerName: p.employerName,
                    employerPhone: p.employerPhone,
                    employerAddress: newAddress,
                    manualAddressEntered: p.manualAddressEntered,
                }
                dispatch(setEmploymentInfo(newEmploymentInfo));
            }
        });
    };

    const setBusinessType = (id: string, newType: string) => {
        previousEmployment.forEach(p => {
            if (p.displayId == id) {
                let newEmploymentInfo: employmentInfo = {
                    displayId: p.displayId,
                    id: p.id,
                    status: p.status,
                    type: p.type,
                    occupation: p.occupation,
                    occupationIndustry: p.occupationIndustry,
                    jobTitle: p.jobTitle,
                    ownershipType: p.ownershipType,
                    businessType: newType,
                    industryYears: p.industryYears,
                    industryMonths: p.industryMonths,
                    employmentYears: p.employmentYears,
                    employmentMonths: p.employmentMonths,
                    notWorkingYears: p.notWorkingYears,
                    notWorkingMonths: p.notWorkingMonths,

                    employerName: p.employerName,
                    employerPhone: p.employerPhone,
                    employerAddress: p.employerAddress,
                    manualAddressEntered: p.manualAddressEntered,
                }
                dispatch(setEmploymentInfo(newEmploymentInfo));
            }
        });
    };

    const setNotWorkingLength = (id: string, years?: string, months?: string) => {
        previousEmployment.forEach(p => {
            if (p.displayId == id) {
                let newEmploymentInfo: employmentInfo = {
                    displayId: p.displayId,
                    id: p.id,
                    status: p.status,
                    type: p.type,
                    occupation: p.occupation,
                    occupationIndustry: p.occupationIndustry,
                    jobTitle: p.jobTitle,
                    ownershipType: p.ownershipType,
                    businessType: p.businessType,
                    industryYears: p.industryYears,
                    industryMonths: p.industryMonths,
                    employmentYears: p.employmentYears,
                    employmentMonths: p.employmentMonths,
                    notWorkingYears: years != undefined ? +years : p.notWorkingYears,
                    notWorkingMonths: months != undefined ? +months : p.notWorkingMonths,

                    employerName: p.employerName,
                    employerPhone: p.employerPhone,
                    employerAddress: p.employerAddress,
                    manualAddressEntered: p.manualAddressEntered,
                }
                dispatch(setEmploymentInfo(newEmploymentInfo));
            }
        });
    };

    const clearEmploymentFields = (id: number, displayId: string, status: string) => {
        let updatedEmploymentInfo: currentEmploymentInfo = {
            id: id,
            displayId: displayId,
            visible: true,
            status: status,
            type: "",
            occupation: "",
            occupationIndustry: "",
            jobTitle: "",
            ownershipType: "",
            businessType: "",
            hasBusinessLending: "",
            industryYears: undefined,
            industryMonths: undefined,
            employmentYears: undefined,
            employmentMonths: undefined,
            notWorkingYears: undefined,
            notWorkingMonths: undefined,
            payrollType: "",
            income: 0,
            incomeId: 0,
            incomeText: "",
            payrollFrequency: "",
            noRetirementIncome: false,
            hasOvertimeIncome: false,
            overtimeIncomeId: 0,
            overtimeIncome: 0,
            overtimeIncomeText: "",
            overtimeIncomeFrequency: "",
            hasBonusIncome: false,
            bonusIncomeId: 0,
            bonusIncome: 0,
            bonusIncomeText: "",
            bonusIncomeFrequency: "",
            hasCommissionIncome: false,
            commissionIncomeId: 0,
            commissionIncome: 0,
            commissionIncomeText: "",
            commissionIncomeFrequency: "",
            hasTipsIncome: false,
            tipsIncomeId: 0,
            tipsIncome: 0,
            tipsIncomeText: "",
            tipsIncomeFrequency: "",
            otherIncome: [],

            employerName: "",
            employerPhone: "",
            employerAddress: {
                displayId: "",
                id: 0,
                streetNumber: "",
                streetName: "",
                streetType: "",
                streetDirection: "",
                unitType: "",
                unitNumber: "",
                city: "",
                province: "",
                postalCode: "",
                country: "",
            },
            manualAddressEntered: false,
            isProbation: "",
            isFamilyOwned: "",
            isOnLeave: "",
            leaveType: "",
            otherLeaveDescription: "",
            workReturnDate: ""
        }
        dispatch(setEmploymentInfo(updatedEmploymentInfo));
    }

    useEffect(() => {
        let employerErrors: string[][] = [];
        for (let i = 0; i < empErrors.length; i++) {
            const errorObj = empErrors[i];
            let currentEmp = [];
            for (const key in errorObj) {
                currentEmp.push(key);
            }
            employerErrors.push(currentEmp);
        }
        setEmpAddrErrors(employerErrors);
    }, [empErrors]);

    const confirmDeletion = (id: string) => {
        confirmDialog({
            message: <p style={{ marginLeft: -14 }}>Are you sure you would like to remove this employment?</p>,
            header: <h2 className="flex"><img src="assets/error-icon.svg" style={{ width: 24, marginRight: 12 }} />Remove Employment?</h2>,
            defaultFocus: 'reject',
            acceptClassName: style.confirm,
            acceptLabel: "Remove Employment",
            rejectClassName: style.cancel,
            rejectLabel: "Cancel",
            accept: () => {
                //Remove Employment
                dispatch(removePreviousEmployment(id));
            },
            draggable: false,
            closable: false
        });
    };

    const previousEmploymentList = () => {
        return (
            <AnimatePresence>
                {previousEmployment.map((position: any) => (
                    <motion.div
                        key={position.displayId}
                        initial={{ y: -100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ type: "linear" }}
                    >
                        <div className="flex flex-row justify-between mt-5">
                            <label htmlFor="" style={{ alignContent: "center", fontSize: 20 }}>Previous Employer {position.displayId}</label>
                            <button
                                type="button"
                                onClick={() => {
                                    if (position.status != "") {
                                        confirmDeletion(position.displayId);
                                    } else {
                                        dispatch(removePreviousEmployment(position.displayId));
                                    }
                                }}
                                className="secondary sm:w-28 h-11 px-6 ml-3"
                                style={{ color: 'var(--brand-blue)', background: "transparent", width: 125 }}>
                                <img
                                    className="mr-3"
                                    src="assets/remove-item.svg"
                                    alt="tooltip"
                                    width={20}
                                    height={20}
                                />
                                Remove
                            </button>
                        </div>

                        <div className="flex md:flex-row flex-col">
                            <div className={clsx(style.twoColFirst, "mt-5")}>
                                <label htmlFor="employmentStatus">Employment Status</label>
                                <div className="flex relative">
                                    <span>
                                        <img
                                            src="assets/error-icon.svg"
                                            className={clsx(
                                                empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentStatus"] != undefined
                                                    ? ""
                                                    : "hidden",
                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                            )}
                                            alt="error"
                                            height={5}
                                            width={5}
                                        />
                                    </span>
                                    <select
                                        id={"employmentStatus" + position.displayId}
                                        onChange={(e) => {
                                            setEmploymentStatus(position.displayId, e.target.value)
                                            if (e.target.value === "unemployed" || e.target.value === "retired") {
                                                clearEmploymentFields(position.id, position.displayId, e.target.value);
                                            }
                                        }}
                                        value={position.status}
                                        className={clsx(
                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentStatus"] != undefined
                                                ? "error"
                                                : "",
                                            "mt-2 w:[220px] md:w-[305px]"
                                        )}
                                        style={{ alignItems: 'center' }}
                                    >
                                        <option hidden value="">
                                            Choose...
                                        </option>
                                        <option value="employed">Employed</option>
                                        <option value="selfemployed">Self Employed</option>
                                        <option value="retired">Retired</option>
                                        <option value="unemployed">Not Employed</option>
                                    </select>
                                </div>
                                {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentStatus"] != undefined && (
                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                        {empErrors[position.displayId - 1]["employmentStatus"]}
                                    </span>
                                )}
                            </div>
                            {(position?.status === "employed" || position?.status === "selfemployed") &&
                                <>
                                    <div className="mt-5">
                                        <label htmlFor="employmentType">Employment type</label>
                                        <div className="flex relative">
                                            <span>
                                                <img
                                                    src="assets/error-icon.svg"
                                                    className={clsx(
                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentType"] != undefined
                                                            ? ""
                                                            : "hidden",
                                                        "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                    )}
                                                    alt="error"
                                                    height={5}
                                                    width={5}
                                                />
                                            </span>
                                            <select
                                                id={"employmentType" + position.displayId}
                                                // {...methods.register("employmentType", {
                                                //     required: "Select an option",
                                                //     onChange: employmentTypeHandler,
                                                // })}
                                                onChange={(e) => {
                                                    setEmploymentType(position.displayId, e.target.value)
                                                }}
                                                value={position.type}
                                                className={clsx(
                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentType"] != undefined
                                                        ? "error"
                                                        : "",
                                                    "mt-2 w:[220px] md:w-[305px]"
                                                )}
                                                style={{ alignItems: 'center' }}
                                            >
                                                <option hidden value="">
                                                    Choose...
                                                </option>
                                                <option value="fulltime">Full-time</option>
                                                <option value="parttime">Part-time</option>
                                                <option value="seasonalfullparttime">Seasonal Full/Part Time</option>
                                                <option value="contract">Contract</option>
                                                <option value="selfemployed">Self Employed</option>
                                            </select>
                                        </div>
                                        {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentType"] != undefined && (
                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                {empErrors[position.displayId - 1]["employmentType"]}
                                            </span>
                                        )}
                                    </div>
                                </>
                            }
                            {position?.status == "unemployed" &&
                                <div className="mt-5">
                                    <label htmlFor="jobTitle">How long have you been not employed?</label>
                                    <div className="flex relative">
                                        <span>
                                            <img
                                                src="assets/error-icon.svg"
                                                className={clsx(
                                                    "hidden",
                                                    "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                )}
                                                alt="error"
                                                height={5}
                                                width={5}
                                            />
                                        </span>
                                        <input
                                            className={clsx(
                                                empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["notWorkingYears"] != undefined ?
                                                    "mt-2 error" : "mt-2", style.residentPeriod
                                            )}
                                            type="number"
                                            min={0}
                                            placeholder="year(s)"
                                            max={99}
                                            id={"notWorkingYears" + position.displayId}
                                            value={position.notWorkingYears != undefined ? position.notWorkingYears : ""}
                                            onChange={(e) => {
                                                if (e.target.value.charAt(0) == "0") {
                                                    e.target.value = e.target.value.slice(1, 2);
                                                } else {
                                                    e.target.value = e.target.value.slice(0, 2);
                                                }
                                                setNotWorkingLength(position.displayId, e.target.value)
                                            }}
                                        />
                                        {position.notWorkingYears != undefined &&
                                            <span style={{ position: "absolute", top: "19px", left: position.notWorkingYears.toString().length > 1 ? "28px" : "22px" }}>year{position.notWorkingYears != 1 ? "s" : ""}</span>
                                        }
                                        <input
                                            className={clsx(
                                                empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["notWorkingMonths"] != undefined ?
                                                    "mt-2 ml-2 error" : "mt-2 ml-2", style.residentPeriod
                                            )}
                                            type="number"
                                            min={0}
                                            max={11}
                                            placeholder="month(s)"
                                            id={"notWorkingMonths" + position.displayId}
                                            value={position.notWorkingMonths != undefined ? position.notWorkingMonths : ""}
                                            onChange={(e) => {
                                                if (e.target.value.charAt(0) == "0") {
                                                    e.target.value = e.target.value.slice(1, 2);
                                                } else {
                                                    e.target.value = e.target.value.slice(0, 2);
                                                }
                                                if (+e.target.value > 11) {
                                                    e.target.value = "11"
                                                }
                                                setNotWorkingLength(position.displayId, undefined, e.target.value)
                                            }}
                                        />
                                        {position.notWorkingMonths != undefined &&
                                            <span style={{ position: "absolute", top: "19px", left: position.notWorkingMonths.toString().length > 1 ? "185px" : "180px" }}>month{position.notWorkingMonths != 1 ? "s" : ""}</span>
                                        }
                                    </div>
                                    {((empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["notWorkingYears"] != undefined) || (empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["notWorkingMonths"] != undefined)) && (
                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                            Enter your time unemployed.
                                        </span>
                                    )}
                                </div>
                            }
                            {position?.status == "retired" &&
                                <div className="mt-5">
                                    <label htmlFor="jobTitle">How long have you been retired?</label>
                                    <div className="flex relative">
                                        <span>
                                            <img
                                                src="assets/error-icon.svg"
                                                className={clsx(
                                                    "hidden",
                                                    "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                )}
                                                alt="error"
                                                height={5}
                                                width={5}
                                            />
                                        </span>
                                        <input
                                            className={clsx(
                                                empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["notWorkingYears"] != undefined ?
                                                    "mt-2 error" : "mt-2", style.residentPeriod
                                            )}
                                            type="number"
                                            min={0}
                                            placeholder="year(s)"
                                            max={99}
                                            id={"notWorkingYears" + position.displayId}
                                            value={position != undefined ? position.notWorkingYears : ""}
                                            onChange={(e) => {
                                                if (e.target.value.charAt(0) == "0") {
                                                    e.target.value = e.target.value.slice(1, 2);
                                                } else {
                                                    e.target.value = e.target.value.slice(0, 2);
                                                }
                                                setNotWorkingLength(position.displayId, e.target.value)
                                            }}
                                        />
                                        {position.notWorkingYears != undefined &&
                                            <span style={{ position: "absolute", top: "19px", left: position.notWorkingYears.toString().length > 1 ? "28px" : "22px" }}>year{position.notWorkingYears != 1 ? "s" : ""}</span>
                                        }
                                        <input
                                            className={clsx(
                                                empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["notWorkingYears"] != undefined ?
                                                    "mt-2 ml-2 error" : "mt-2 ml-2", style.residentPeriod
                                            )}
                                            type="number"
                                            min={0}
                                            max={11}
                                            placeholder="month(s)"
                                            id={"notWorkingMonths" + position.displayId}
                                            value={position != undefined ? position.notWorkingMonths : ""}
                                            onChange={(e) => {
                                                if (e.target.value.charAt(0) == "0") {
                                                    e.target.value = e.target.value.slice(1, 2);
                                                } else {
                                                    e.target.value = e.target.value.slice(0, 2);
                                                }
                                                if (+e.target.value > 11) {
                                                    e.target.value = "11"
                                                }
                                                setNotWorkingLength(position.displayId, undefined, e.target.value)
                                            }}
                                        />
                                        {position.notWorkingMonths != undefined &&
                                            <span style={{ position: "absolute", top: "19px", left: position.notWorkingMonths.toString().length > 1 ? "185px" : "180px" }}>month{position.notWorkingMonths != 1 ? "s" : ""}</span>
                                        }
                                    </div>
                                    {((empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["notWorkingYears"] != undefined) || (empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["notWorkingMonths"] != undefined)) && (
                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                            Enter your time in retirement.
                                        </span>
                                    )}
                                </div>
                            }
                        </div>

                        {(position?.status === "employed" || position?.status === "selfemployed") &&
                            <>
                                <div className="flex md:flex-row flex-col">
                                    <div className={style.twoColFirst}>
                                        <div className={clsx(
                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["occupation"] != undefined
                                                ? "error"
                                                : "",
                                            "mt-5 w:[220px] md:w-[305px]"
                                        )}>
                                            <label htmlFor="occupation">Occupation</label>
                                            <div className="flex relative">
                                                <span>
                                                    <SearchableDropdown
                                                        options={sessionData.occupations}
                                                        label="name"
                                                        id={"occupation" + position.displayId}
                                                        selectedVal={position.occupation}
                                                        handleChange={(val: any) => {
                                                            let category = sessionData.occupations?.find((x) => x.name == val)?.category;
                                                            let updateOccupationCategory = [...occupationCategory];
                                                            updateOccupationCategory[position.displayId - 1] = category ? category : "";
                                                            setOccupationCategory(updateOccupationCategory);

                                                            setOccupation(position.displayId, val, category ? category : "");
                                                        }}
                                                        customStyle={{ paddingRight: 40 }}
                                                        disableForm={true}
                                                        fetching={false}
                                                    />
                                                </span>
                                            </div>
                                            <small
                                                className={clsx(
                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["occupation"] != undefined ? "hidden" : "",
                                                    "text-gray-400"
                                                )}
                                            >
                                                <b>Example</b>: Financial Planner, Marketing Manager
                                            </small>
                                            {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["occupation"] != undefined && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {empErrors[position.displayId - 1]["occupation"]}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-5">
                                        <label htmlFor="jobTitle">Job title</label>
                                        <div className="flex relative">
                                            <span>
                                                <img
                                                    src="assets/error-icon.svg"
                                                    className={clsx(
                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["jobTitle"]
                                                            ? ""
                                                            : "hidden",
                                                        "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                    )}
                                                    alt="error"
                                                    height={5}
                                                    width={5}
                                                />
                                            </span>
                                            <input
                                                className={clsx(
                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["jobTitle"] != undefined
                                                        ? "error"
                                                        : "",
                                                    "mt-2 w:[220px] md:w-[305px]"
                                                )}
                                                type="text"
                                                id={"jobTitle" + position.displayId}
                                                onChange={(e) => {
                                                    setJobTitle(position.displayId, e.target.value)
                                                }}
                                                value={position.jobTitle}
                                                maxLength={30}
                                            />
                                        </div>
                                        {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["jobTitle"] != undefined && (
                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                {empErrors[position.displayId - 1]["jobTitle"]}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {(position.status === "selfemployed") &&
                                    <div className="flex md:flex-row flex-col">
                                        <div className={clsx(style.twoColFirst, "mt-5")}>
                                            <label htmlFor="ownership">Type of Ownership</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["ownership"] != undefined
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <select
                                                    id={"ownership" + position.displayId}
                                                    onChange={(e) => {
                                                        setOwnership(position.displayId, e.target.value)
                                                    }}
                                                    value={position.ownershipType}
                                                    className={clsx(
                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["ownership"] != undefined
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    style={{ alignItems: 'center' }}
                                                >
                                                    <option hidden value="">
                                                        Choose...
                                                    </option>
                                                    <option value="soleproprietor">Sole Proprietorship</option>
                                                    <option value="partnership">Partnership</option>
                                                    <option value="corporation">Corporation</option>
                                                </select>
                                            </div>
                                            {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["ownership"] != undefined && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {empErrors[position.displayId - 1]["ownership"]}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-5">
                                            <label htmlFor="jobTitle">Business Type</label>
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["businessType"] != undefined
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <input
                                                    className={clsx(
                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["businessType"] != undefined
                                                            ? "error"
                                                            : "",
                                                        "mt-2 w:[220px] md:w-[305px]"
                                                    )}
                                                    type="text"
                                                    id={"businessType" + position.displayId}
                                                    onChange={(e) => {
                                                        setBusinessType(position.displayId, e.target.value)
                                                    }}
                                                    value={position.businessType}
                                                    maxLength={30}
                                                />
                                            </div>
                                            {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["businessType"] != undefined && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {empErrors[position.displayId - 1]["businessType"]}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                }
                                <div className="flex md:flex-row flex-col">
                                    <div className={clsx(style.twoColFirst, "mt-5")}>
                                        <label htmlFor="jobTitle">Time in industry</label>
                                        <div className="flex relative">
                                            <span>
                                                <img
                                                    src="assets/error-icon.svg"
                                                    className={clsx(
                                                        "hidden",
                                                        "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                    )}
                                                    alt="error"
                                                    height={5}
                                                    width={5}
                                                />
                                            </span>
                                            <input
                                                className={clsx(
                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["industryLengthYears"] != undefined
                                                        ? "mt-2 error"
                                                        : "mt-2",
                                                    style.residentPeriod
                                                )}
                                                type="number"
                                                min={0}
                                                placeholder="year(s)"
                                                max={99}
                                                id={"industryLengthYears" + position.displayId}
                                                value={position.industryYears != undefined ? position.industryYears : ""}
                                                onChange={(e) => {
                                                    if (e.target.value.charAt(0) == "0") {
                                                        e.target.value = e.target.value.slice(1, 2);
                                                    } else {
                                                        e.target.value = e.target.value.slice(0, 2);
                                                    }
                                                    setIndustryLength(position.displayId, e.target.value)
                                                }}
                                            />
                                            {position.industryYears != undefined &&
                                                <span style={{ position: "absolute", top: "19px", left: position.industryYears.toString().length > 1 ? "28px" : "22px" }}>year{position.industryYears != 1 ? "s" : ""}</span>
                                            }
                                            <input
                                                className={clsx(
                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["industryLengthMonths"] != undefined
                                                        ? "mt-2 ml-2 error"
                                                        : "mt-2 ml-2",
                                                    style.residentPeriod
                                                )}
                                                type="number"
                                                min={0}
                                                max={11}
                                                placeholder="month(s)"
                                                id={"industryLengthMonths" + position.displayId}
                                                value={position.industryMonths != undefined ? position.industryMonths : ""}
                                                onChange={(e) => {
                                                    if (e.target.value.charAt(0) == "0") {
                                                        e.target.value = e.target.value.slice(1, 2);
                                                    } else {
                                                        e.target.value = e.target.value.slice(0, 2);
                                                    }
                                                    if (+e.target.value > 11) {
                                                        e.target.value = "11"
                                                    }
                                                    setIndustryLength(position.displayId, undefined, e.target.value)
                                                }}
                                            />
                                            {position.industryMonths != undefined &&
                                                <span style={{ position: "absolute", top: "19px", left: position.industryMonths.toString().length > 1 ? "185px" : "180px" }}>month{position.industryMonths != 1 ? "s" : ""}</span>
                                            }
                                        </div>
                                        {((empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["industryLengthYears"] != undefined) || (empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["industryLengthMonths"] != undefined)) && (
                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                Enter your time at this industry.
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-5">
                                        <label htmlFor="jobTitle">Duration of employment</label>
                                        <div className="flex relative">
                                            <span>
                                                <img
                                                    src="assets/error-icon.svg"
                                                    className={clsx(
                                                        "hidden",
                                                        "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                    )}
                                                    alt="error"
                                                    height={5}
                                                    width={5}
                                                />
                                            </span>
                                            <input
                                                className={clsx(
                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentYears"] != undefined
                                                        ? "mt-2 error"
                                                        : "mt-2",
                                                    style.residentPeriod
                                                )}
                                                type="number"
                                                min={0}
                                                placeholder="year(s)"
                                                max={99}
                                                id={"employmentLengthYears" + position.displayId}
                                                value={position.employmentYears != undefined ? position.employmentYears : ""}
                                                onChange={(e) => {
                                                    if (e.target.value.charAt(0) == "0") {
                                                        e.target.value = e.target.value.slice(1, 2);
                                                    } else {
                                                        e.target.value = e.target.value.slice(0, 2);
                                                    }
                                                    setEmploymentLength(position.displayId, e.target.value)
                                                }}
                                            />
                                            {position.employmentYears != undefined &&
                                                <span style={{ position: "absolute", top: "19px", left: position.employmentYears.toString().length > 1 ? "28px" : "22px" }}>year{position.employmentYears != 1 ? "s" : ""}</span>
                                            }
                                            <input
                                                className={clsx(
                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentYears"] != undefined
                                                        ? "mt-2 ml-2 error"
                                                        : "mt-2 ml-2",
                                                    style.residentPeriod
                                                )}
                                                type="number"
                                                min={0}
                                                max={11}
                                                placeholder="month(s)"
                                                id={"employmentLengthMonths" + position.displayId}
                                                value={position.employmentMonths != undefined ? position.employmentMonths : ""}
                                                onChange={(e) => {
                                                    if (e.target.value.charAt(0) == "0") {
                                                        e.target.value = e.target.value.slice(1, 2);
                                                    } else {
                                                        e.target.value = e.target.value.slice(0, 2);
                                                    }
                                                    if (+e.target.value > 11) {
                                                        e.target.value = "11"
                                                    }
                                                    setEmploymentLength(position.displayId, undefined, e.target.value)
                                                }}
                                            />
                                            {position.employmentMonths != undefined &&
                                                <span style={{ position: "absolute", top: "19px", left: position.employmentMonths.toString().length > 1 ? "185px" : "180px" }}>month{position.employmentMonths != 1 ? "s" : ""}</span>
                                            }
                                        </div>
                                        {((empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentYears"] != undefined) || (empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employmentMonths"] != undefined)) && (
                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                Enter your time with this employer.
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </>
                        }

                        {(position?.status === "employed" || position?.status === "selfemployed") &&
                            <>
                                {/* Employer Name & Phone Number*/}

                                <hr className="mt-5" />

                                <div className="flex md:flex-row flex-col">
                                    <div className={clsx(style.twoColFirst, "mt-5")}>
                                        <label htmlFor="employerName">Employer name</label>
                                        <div className="flex relative">
                                            <span>
                                                <img
                                                    src="assets/error-icon.svg"
                                                    className={clsx(
                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employerName"] != undefined
                                                            ? ""
                                                            : "hidden",
                                                        "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                    )}
                                                    alt="error"
                                                    height={5}
                                                    width={5}
                                                />
                                            </span>
                                            <input
                                                className={clsx(
                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employerName"] != undefined
                                                        ? "error"
                                                        : "",
                                                    "mt-2 w:[220px] md:w-[305px]"
                                                )}
                                                type="text"
                                                id={"employerName" + position.displayId}
                                                onChange={(e) => {
                                                    setEmployerName(position.displayId, e.target.value)
                                                }}
                                                value={position.employerName}
                                                maxLength={30}
                                            />
                                        </div>
                                        {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employerName"] != undefined && (
                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                {empErrors[position.displayId - 1]["employerName"]}
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-5">
                                        <label htmlFor="employerPhoneNumber">Employer phone number</label>
                                        <div className="flex relative">
                                            <span>
                                                <img
                                                    src="assets/error-icon.svg"
                                                    className={clsx(
                                                        empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employerPhoneNumber"] != undefined
                                                            ? ""
                                                            : "hidden",
                                                        "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[275px]"
                                                    )}
                                                    alt="error"
                                                    height={5}
                                                    width={5}
                                                />
                                            </span>
                                            <InputMask
                                                type="tel"
                                                id={"employerPhone" + position.displayId}
                                                placeholder="(000) 000-0000"
                                                alwaysShowMask={false}
                                                maskChar={null}
                                                value={position.employerPhone}
                                                mask="(999) 999-9999"
                                                className={clsx(
                                                    empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employerPhoneNumber"] != undefined
                                                        ? "error"
                                                        : "",
                                                    "mt-2 w:[200px] md:w-[305px]"
                                                )}
                                                onChange={(e) => {
                                                    setEmployerPhone(position.displayId, e.target.value)
                                                }}
                                            // value={position ? position.employerPhone : ""}
                                            // {...methods.register("employerPhoneNumber", {
                                            //     required: "Enter a valid phone number",
                                            //     pattern: {
                                            //         value:
                                            //             // eslint-disable-next-line no-useless-escape
                                            //             /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
                                            //         message: "Enter a valid phone number",
                                            //     },
                                            // })}
                                            />
                                        </div>
                                        {empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["employerPhoneNumber"] != undefined && (
                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                {empErrors[position.displayId - 1]["employerPhoneNumber"]}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Employer Address */}

                                <div className="col-span-1 mt-5">
                                    <div className={""}>
                                        <label htmlFor="search">Employer Address</label>
                                        <div className="flex md:flex-row flex-col justify-between">
                                            <div className="flex relative">
                                                <span>
                                                    <img
                                                        src="assets/magnifying-glass-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.autoAddress || (empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["autoAddress"] != undefined)
                                                                ? "hidden"
                                                                : "",
                                                            "w-5 h-5 absolute top-5 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                        )}
                                                        alt="search"
                                                        height={5}
                                                        width={5}
                                                    />
                                                    <img
                                                        src="assets/error-icon.svg"
                                                        className={clsx(
                                                            methods.formState?.errors?.autoAddress || (empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["autoAddress"] != undefined)
                                                                ? ""
                                                                : "hidden",
                                                            "w-4 h-4 absolute top-6 max-xxs:ml-[210px] max-sm:ml-[300px] xs:max-md:ml-[370px] md:ml-[430px]"
                                                        )}
                                                        alt="error"
                                                        height={5}
                                                        width={5}
                                                    />
                                                </span>
                                                <input
                                                    autoComplete="nope"
                                                    className={clsx(
                                                        methods.formState?.errors?.autoAddress?.message || (empErrors[position.displayId - 1] != undefined && empErrors[position.displayId - 1]["autoAddress"] != undefined)
                                                            ? "error"
                                                            : "",
                                                        "mt-2"
                                                    )}
                                                    id={"empAddressSearch" + position.displayId}
                                                    type="text"
                                                    placeholder="Start typing address"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex relative mt-2">
                                            <p className="active underline cursor-pointer" onClick={() => toggleManualAddress(position.displayId)}>Enter address manually</p>
                                        </div>

                                        {addressSelectionError && (
                                            <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                You must select an address from the list of options
                                            </span>
                                        )}
                                        <div className={clsx(position.employerAddress.city != "" ? "" : "hidden")}>
                                            <p id={"formatted-address" + position.displayId}></p>
                                            {position.employerAddress.unitNumber != "" && <><span >{position.employerAddress.unitNumber}-</span></>}
                                            {position.employerAddress.streetNumber != "" && <><span >{position.employerAddress.streetNumber} {position.employerAddress.streetName} {position.employerAddress.streetType}
                                                {position.employerAddress.streetDirection != "" && " " + position.employerAddress.streetDirection}</span><br /></>}
                                            {position.employerAddress.city != "" && <><span >{position.employerAddress.city}</span> &nbsp;</>}
                                            {position.employerAddress.province != "" && <><span >{position.employerAddress.province}</span>&nbsp;</>}
                                            {position.employerAddress.postalCode != "" && <><span >{position.employerAddress.postalCode}</span><br /></>}
                                            {position.employerAddress.country != "" && <span >{position.employerAddress.country}</span>}
                                        </div>
                                        <div
                                            className={clsx(
                                                position.manualAddressEntered ? "" : "hidden",
                                                "flex relative"
                                            )}
                                        >
                                            <div className="mt-5">
                                                <ManualAddress passAddressData={getAddressData} addressData={position.employerAddress} addressErrors={empAddrErrors} errorIndex={position.displayId - 1} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={clsx(
                                    position.employerAddress.city != ""
                                        ? "mt-5"
                                        : "",
                                    "flex md:flex-row flex-col w-max mb-5"
                                )}>
                                </div>
                            </>
                        }
                        <hr className="mt-5" />

                    </motion.div>
                ))}
            </AnimatePresence>
        )
    };

    return (
        <>
            <SlideTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <ConfirmDialog />
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Address Header"
                    >
                        <div className="max-md:ml-2 mb-4">
                            <h1 aria-label="Address Header">What is your employment history?</h1>
                            <p className="sub-heading">Tell us more about your previous employer. We are looking for three years of employment history.</p>
                        </div>
                        <div className={"flex flex-row"}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                >
                                    {displayError &&
                                        <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
                                            <img src="assets/error-info-icon.svg" alt="error" />
                                            <span className="text-sm ml-2 mt-1">
                                                {errorMessage}
                                            </span>
                                        </div>
                                    }
                                    <div className="mt-5 flex flex-col-reverse mb-4">
                                        <label></label>
                                        <input
                                            id="insufficientprompt"
                                            type="checkbox"
                                            checked={insufficientHistory}
                                            onChange={(e) => {
                                                setDisplayError(false);
                                                setInsufficientHistory(e.target.checked)
                                                dispatch(toggleNoPreviousEmployment(e.target.checked));
                                            }}
                                            className="accent"
                                        />
                                        <label htmlFor="insufficientprompt">
                                            <p className="sub-heading ml-7 -mt-5">
                                                I have less than three years of employment history
                                            </p>
                                        </label>
                                    </div>

                                    {previousEmployment != undefined && previousEmployment.length > 0 &&
                                        <>
                                            {previousEmploymentList()}
                                        </>

                                    }

                                    <div className="mb-5">
                                        <button
                                            type="button"
                                            onClick={addPrevEmployment}
                                            className="secondary mt-5 w-full sm:w-28 h-11 px-6"
                                            style={{ width: '-webkit-fill-available', color: 'var(--brand-blue)' }}>
                                            <img
                                                className="mr-3"
                                                style={{ marginBottom: 2 }}
                                                src="assets/add-circle.svg"
                                                alt="tooltip"
                                                width={15}
                                                height={15}
                                            />Add Another Employer
                                        </button>
                                    </div>


                                    <div className="flex max-sm:hidden">
                                        <div className="mb-5 mr-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-169 sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                disabled={isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                Continue
                                            </button>
                                        </div>
                                    </div>
                                    <div className="sm:hidden">
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                disabled={isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                Continue
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-full sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </SlideTransition>
        </>
    );
}